import { Navigate, Route } from 'react-router-dom';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';

import { CoreDumpsPage } from '@internal/plugin-core-dumps';
import { VersionsPage } from '@internal/plugin-catalog-agent';

import { orgPlugin } from '@backstage/plugin-org';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { catalogColumns } from './components/catalog/catalogColumns';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { MaintainersPickerFieldExtension } from './scaffolder/MaintainersPicker';
import { NamespacePickerFieldExtension } from './scaffolder/NamespacePicker';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { gitlabAuthApiRef } from '@backstage/core-plugin-api';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import { lightTheme, darkTheme } from './themes';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
    });
  },
  themes: [
    {
      id: 'light',
      title: 'Light theme',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={lightTheme} children={children} />
      ),
    },
    {
      id: 'dark',
      title: 'Dark theme',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={darkTheme} children={children} />
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const guestEnabled =
        configApi.getOptionalBoolean('auth.enableGuestSignIn') ?? false;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const providers: any[] = [
        {
          id: 'gitlab-auth-provider',
          title: 'GitLab',
          message: 'Sign in using GitLab',
          apiRef: gitlabAuthApiRef,
        },
      ];

      if (guestEnabled) {
        providers.push('guest');
      }

      return <SignInPage {...props} auto providers={providers} />;
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={<CatalogIndexPage columns={catalogColumns} />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <MaintainersPickerFieldExtension />
        <NamespacePickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/core-dumps" element={<CoreDumpsPage />} />
    <Route path="/versions" element={<VersionsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
